import { authHeaders, getBackendUrl } from "../lib/http";
import { useActiveShopId } from "../store/shop.store";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Star, Loader2, RefreshCw, ChevronLeft, ChevronRight,
  AlertCircle, MessageSquare, Calendar,
} from "lucide-react";
import { clsx } from "clsx";

interface EtsyReview {
  shop_id: number;
  listing_id: number;
  transaction_id: number;
  buyer_user_id: number;
  rating: number;
  review: string;
  created_timestamp: number;
  updated_timestamp: number;
  image_url_fullxfull?: string;
}

interface EtsyReviewsResponse {
  count: number;
  results: EtsyReview[];
}

async function fetchReviews(offset: number, limit: number): Promise<EtsyReviewsResponse> {
  const params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
  const res = await fetch(`${getBackendUrl()}/api/etsy/receipts/reviews/shop?${params}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error("Yorumlar alınamadı");
  const data = await res.json();
  return data.data;
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={clsx("h-4 w-4", i <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-200")}
        />
      ))}
    </div>
  );
}

export default function EtsyReviewsPage() {
  const queryClient = useQueryClient();
  const [offset, setOffset] = useState(0);
  const limit = 25;
  const shopId = useActiveShopId();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["etsy-reviews", shopId, offset],
    queryFn: () => fetchReviews(offset, limit),
  });

  const reviews = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  // Calculate average rating
  const avgRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : "0";

  const ratingDist = [5, 4, 3, 2, 1].map((r) => ({
    rating: r,
    count: reviews.filter((rev) => rev.rating === r).length,
  }));

  function formatDate(timestamp: number) {
    return new Date(timestamp * 1000).toLocaleDateString("tr-TR", {
      day: "numeric", month: "long", year: "numeric",
    });
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-orange-500" />
            Mağaza Yorumları
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Müşterilerinizden gelen yorumlar ({totalCount} yorum)
          </p>
        </div>
        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ["etsy-reviews"] })}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4" />
          Yenile
        </button>
      </div>

      {/* Summary Card */}
      {reviews.length > 0 && (
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex items-center gap-8">
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">{avgRating}</p>
              <StarRating rating={Math.round(Number(avgRating))} />
              <p className="mt-1 text-xs text-gray-500">Ortalama Puan</p>
            </div>
            <div className="flex-1 space-y-1">
              {ratingDist.map((d) => (
                <div key={d.rating} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-3">{d.rating}</span>
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-yellow-400 rounded-full"
                      style={{ width: `${reviews.length ? (d.count / reviews.length) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-400 w-8 text-right">{d.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-sm text-red-700">{(error as Error).message}</p>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      )}

      {/* Empty */}
      {!isLoading && reviews.length === 0 && (
        <div className="text-center py-16">
          <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Henüz yorum yok</p>
        </div>
      )}

      {/* Reviews List */}
      {!isLoading && reviews.length > 0 && (
        <div className="space-y-3">
          {reviews.map((review) => (
            <div
              key={`${review.transaction_id}-${review.created_timestamp}`}
              className="rounded-xl border border-gray-200 bg-white p-4"
            >
              <div className="flex items-center justify-between mb-2">
                <StarRating rating={review.rating} />
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <Calendar className="h-3 w-3" />
                  {formatDate(review.created_timestamp)}
                </span>
              </div>

              {review.review && (
                <p className="text-sm text-gray-700 leading-relaxed">{review.review}</p>
              )}

              {!review.review && (
                <p className="text-sm text-gray-400 italic">Yorum metni yok (sadece puan verilmiş)</p>
              )}

              {review.image_url_fullxfull && (
                <img
                  src={review.image_url_fullxfull}
                  alt="Review"
                  className="mt-2 h-24 w-24 rounded-lg object-cover"
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Sayfa {currentPage} / {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
              className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
              Önceki
            </button>
            <button
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= totalCount}
              className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            >
              Sonraki
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
